import React, { useEffect, useMemo, useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Dialog,
  DialogPanel,
  Transition,
} from "@headlessui/react";
import {
  Squares2X2Icon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  DocumentChartBarIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  CpuChipIcon,
  ChartBarSquareIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
  CogIcon,
  HomeIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import {
  CurrencyDollarIcon,
  ChatBubbleBottomCenterTextIcon,
  VideoCameraSlashIcon,
  KeyIcon,
  FolderIcon,
  ChartBarIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useLocation, useNavigate } from "react-router-dom";
import "promise.allsettled/auto";

import SolidarityIcon from "../Assets/Icons/SolidarityIcon";
import RequestsLists from "./Requests/RequestsLists";
import useAuthCheck from "../CheckUser/userAuthCheck";
import SbDriveScoring from "./SBDrive/SbDriveScoring";
import ProgramActivation from "./SBDrive/ProgramActivation";
import ProgramDeactivation from "./SBDrive/ProgramDeactivation";
import OldPaymentRequests from "./Requests/OldPaymentRequests";
import PaymentRequestsAdmin from "../Admin/PaymentRequestsAdmin";
import useSessionCountdown from "../Sessions/useSessionCountDown";
import { CheckToken } from "../API/TokenAPI";
import Modal from "./Modals/Modal";
import SessionReminderModal from "./Modals/SessionReminderModal";
import IdleWarningModal from "./Modals/IdleWarningModal";
import useIdleTimeout from "../Sessions/useIdleTimeout";
import DownloadCreatedReport from "./Requests/Reports/DownloadCreatedReport";
import DocumentMainPage from "../../DocuSeal/DocumentHomePage";
import FundInformation from "./Requests/Reports/AllFunds/FundInformation";
import Performance from "./Requests/Reports/AllFunds/Performance";
import FundFees from "./Requests/Reports/AllFunds/FundFees";
import MotorRenewalMigration from "./Requests/Reports/Migration/MotorRenewalMigration";
import HomePage from "./Home/HomePage";

/* ----------------- data ----------------- */
const navigation = [
  {
    name: "AdminMain",
    icon: KeyIcon,
    current: false,
    departments: ["Underwriting", "IT"],
    children: [
      { name: "PaymentRequestsAdmin", href: "?page=PaymentRequestsAdmin" },
    ],
  },
  {
    name: "Payment Requests",
    href: "#",
    icon: CurrencyDollarIcon,
    current: true,
    departments: ["Finance", "IT"],
    nav: "requests",
  },
  {
    name: "Old Payment Requests",
    href: "#",
    icon: CurrencyDollarIcon,
    current: true,
    departments: ["Finance", "IT"],
    nav: "requests",
  },
  {
    name: "DocumentSignature",
    href: "#",
    icon: DocumentIcon,
    current: true,
    departments: ["Finance", "IT"],
    nav: "DocumentSignature",
  },
  {
    name: "Reports",
    icon: ChartBarIcon,
    current: false,
    departments: ["IT", "Marketing"],
    children: [
      { name: "Transaction Reports", href: "?page=TransactionReports", departments: ["IT"], nav: "accounts" },
      { name: "Fund Information", href: "?page=FundInformation", departments: ["IT"], nav: "accounts" },
      { name: "Performance and Key statistics", href: "?page=PerformanceandKeystatistics", departments: ["IT"], nav: "accounts" },
      { name: "Fund Fees", href: "?page=FundFees", departments: ["IT"], nav: "accounts" },
      { name: "Motor Renewal Migration", href: "?page=MotorRenewalMigration", departments: ["IT"], nav: "accounts" },




      {
        name: "Mobile Notifications",
        href: "#",
        departments: ["IT", "HR"],
        nav: "notifications",
      },
      {
        name: "Customer Files",
        href: "#",
        departments: ["Marketing"],
        nav: "files",
      },
    ],
  },
  {
    name: "Policy Management",
    icon: FolderIcon,
    current: false,
    departments: ["Underwriting", "IT"],
    children: [
      {
        name: "Medical",
        href: "#",
        departments: ["Underwriting"],
        nav: "files",
      },
      { name: "Motor", href: "#", departments: ["Underwriting"], nav: "files" },
      {
        name: "Non-Motor",
        href: "#",
        departments: ["Underwriting"],
        nav: "files",
      },
      { name: "Life", href: "#", departments: ["Underwriting"], nav: "files" },
    ],
  },
  {
    name: "Video Insurance",
    href: "#",
    icon: VideoCameraSlashIcon,
    current: false,
    departments: ["Claims"],
    nav: "files",
  },
  {
    name: "Chatbot Dashboard",
    href: "#",
    icon: ChatBubbleBottomCenterTextIcon,
    current: false,
    departments: ["IT"],
    nav: "files",
  },
  {
    name: "SB Drive",
    icon: FolderIcon,
    current: false,
    departments: ["Underwriting", "IT"],
    children: [
      {
        name: "SB Drive Scoring",
        href: "?page=SBDriveScoring",
        departments: ["Underwriting"],
        nav: "files",
      },
      {
        name: "Program Activation",
        href: "?page=ProgramActivation",
        departments: ["Underwriting"],
        nav: "files",
      },
      {
        name: "Program Deactivation",
        href: "?page=ProgramDeactivation",
        departments: ["Underwriting"],
        nav: "files",
      },
      {
        name: "Non-Motor",
        href: "#",
        departments: ["Underwriting"],
        nav: "files",
      },
      { name: "Life", href: "#", departments: ["Underwriting"], nav: "files" },
    ],
  },
];

// Map section names to new professional icons
const iconMap = {
  "Payment Requests": BanknotesIcon,
  "Old Payment Requests": ClipboardDocumentCheckIcon,
  Reports: DocumentChartBarIcon,
  "Policy Management": BuildingOffice2Icon,
  "Video Insurance": ShieldCheckIcon,
  "Chatbot Dashboard": CpuChipIcon,
  "SB Drive": ChartBarSquareIcon,
  AdminMain: UserGroupIcon,
};

const HubsAccess = [
  {
    name: "Dashboard",
    href: "#",
    current: true,
    departments: [
      "IT",
      "Compliance",
      "Finance",
      "HR",
      "Admin",
      "Claims",
      "Underwriting",
    ],
  },
];

/* ----------------- utils ----------------- */
function classNames(...c) {
  return c.filter(Boolean).join(" ");
}
function niceName(username) {
  if (!username) return "User";
  return username
    .split(".")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : ""))
    .join(" ");
}

/* --------------- component --------------- */
const TheHub = () => {
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false); // NEW: mobile drawer
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebarCollapsed") === "1";
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem("sidebarCollapsed", next ? "1" : "0");
      } catch {}
      return next;
    });
  };

  useEffect(() => {
    const navEntries = performance.getEntriesByType?.("navigation") ?? [];
    if (navEntries.length > 0 && navEntries[0].type === "reload") {
      CheckToken(sessionStorage.getItem("token"), setOpen);
    }
  }, []);

  const { timeLeft, reminderMinutes, resetReminder } = useSessionCountdown({
    setModal: setOpen,
  });

  // 15-minute idle timeout — warning at 14 min, auto-logout at 15 min
  const handleIdleLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("expiry");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("department");
    sessionStorage.removeItem("position");
    sessionStorage.removeItem("hasAccessTo");
    navigate("/");
  };

  const {
    showWarning: idleWarning,
    countdown: idleCountdown,
    reset: resetIdle,
  } = useIdleTimeout({ onTimeout: handleIdleLogout });
  const location = useLocation();
  useAuthCheck();
  const navigate = useNavigate();
  const accessList = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("hasAccessTo") || "[]");
    } catch {
      return [];
    }
  }, []);

  const handleSignOut = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("expiry");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("department");
    sessionStorage.removeItem("position");
    sessionStorage.removeItem("hasAccessTo");
    navigate("/");
  };

  const userNavigation = [{ name: "Sign out", action: handleSignOut }];

  const query = new URLSearchParams(location.search);
  const page = query.get("page") || "Home";

  const hasAccess =
    page === "Home" ||
    accessList.some((name) => name.replace(/\s/g, "") === page);

  const profileImgSrc = (() => {
    const base = (process.env.REACT_APP_PAYMENT_API || "").replace(/\/$/, "");
    const rel = (sessionStorage.getItem("profilePath") || "").replace(
      /^\//,
      "",
    );
    return rel ? `${base}/${rel}` : "";
  })();

  const renderComponent = () => {
    if (!hasAccess) {
      return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
            You do not have access to this page.
          </div>
        </div>
      );
    }

    switch (page) {
      case "Home":
        return <HomePage />;
      case "PaymentRequests":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card>
              <RequestsLists />
            </Card>
          </div>
        );
      case "SBDriveScoring":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card>
              <SbDriveScoring />
            </Card>
          </div>
        );
      case "ProgramActivation":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card>
              <ProgramActivation />
            </Card>
          </div>
        );
      case "ProgramDeactivation":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card>
              <ProgramDeactivation />
            </Card>
          </div>
        );
      case "OldPaymentRequests":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card>
              <OldPaymentRequests />
            </Card>
          </div>
        );
      case "PaymentRequestsAdmin":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card>
              <PaymentRequestsAdmin />
            </Card>
          </div>
        );
      case "TransactionReports":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card>
              <DownloadCreatedReport />
            </Card>
          </div>
        );
      case "FundInformation":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card><FundInformation /></Card>
          </div>
        );
      case "PerformanceandKeystatistics":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card><Performance /></Card>
          </div>
        );
      case "FundFees":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card><FundFees /></Card>
          </div>
        );
      case "MotorRenewalMigration":
        return (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <Card><MotorRenewalMigration /></Card>
          </div>
        );
      case "DocumentSignature":
        return (
          <div className="mx-auto">
            <Card>
              <DocumentMainPage />
            </Card>
          </div>
        );
      default:
        return <HomePage />;
    }
  };

  const pageContent = useMemo(() => renderComponent(), [page, hasAccess]);

  return (
    <>
      {/* ---------- Mobile Drawer (<= lg) ---------- */}
      <Transition show={mobileOpen}>
        <Dialog className="relative z-50 lg:hidden" onClose={setMobileOpen}>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-[1px]"
            aria-hidden="true"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel className="relative mr-16 flex w-full max-w-xs">
              <div className="flex grow flex-col bg-white shadow-2xl">
                {/* Brand row */}
                <div className="sticky top-0 z-10 bg-white/95 backdrop-blur">
                  <div className="flex h-16 items-center justify-between px-4">
                    <div className="flex items-center gap-2">
                      <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-sm">
                        {/* small brand glyph */}
                        <Squares2X2Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          Solidarity Hub
                        </div>
                        <div className="text-[11px] text-gray-500">Menu</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setMobileOpen(false)}
                      className="rounded-md p-2 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      aria-label="Close menu"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                </div>

                {/* Profile */}
                <div className="px-4 pb-3 pt-3">
                  <a
                    href="#"
                    className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {profileImgSrc ? (
                      <img
                        alt=""
                        src={profileImgSrc}
                        className="h-9 w-9 rounded-full object-cover bg-gray-100"
                        onError={(e) =>
                          (e.currentTarget.style.display = "none")
                        }
                      />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-gray-200" />
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-gray-900">
                        {niceName(sessionStorage.getItem("username") || "")}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        {sessionStorage.getItem("department") || ""}
                      </div>
                    </div>
                  </a>
                </div>

                {/* Section label */}
                <div className="px-5 pb-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Navigation
                  </div>
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto px-3 pb-6">
                  <ul className="space-y-1">
                    {navigation
                      .filter((item) => accessList.includes(item.name))
                      .map((item) => {
                        const isCurrent = item.name.replace(/\s/g, "") === page;
                        const Icon = iconMap[item.name] || Squares2X2Icon;

                        if (!item.children) {
                          return (
                            <li key={item.name}>
                              <a
                                href={`?page=${item.name.replace(/\s/g, "")}`}
                                onClick={() => setMobileOpen(false)}
                                aria-current={isCurrent ? "page" : undefined}
                                className={classNames(
                                  isCurrent
                                    ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-100"
                                    : "text-gray-700 hover:bg-gray-50",
                                  "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                                )}
                              >
                                <span
                                  className={classNames(
                                    isCurrent
                                      ? "bg-indigo-100 text-indigo-600"
                                      : "bg-gray-100 text-gray-500 group-hover:text-gray-600",
                                    "grid h-8 w-8 place-items-center rounded-lg",
                                  )}
                                >
                                  <Icon className="h-5 w-5" />
                                </span>
                                <span className="truncate">{item.name}</span>
                              </a>
                            </li>
                          );
                        }

                        // With children
                        return (
                          <li key={item.name} className="rounded-lg">
                            <Disclosure>
                              {({ open }) => {
                                const parentActive =
                                  open ||
                                  item.children.some(
                                    (c) => c.name.replace(/\s/g, "") === page,
                                  );

                                return (
                                  <>
                                    <DisclosureButton
                                      className={classNames(
                                        parentActive
                                          ? "bg-gray-50"
                                          : "hover:bg-gray-50",
                                        "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                                      )}
                                    >
                                      <span
                                        className={classNames(
                                          parentActive
                                            ? "bg-indigo-50 text-indigo-600"
                                            : "bg-gray-100 text-gray-500 group-hover:text-gray-600",
                                          "grid h-8 w-8 place-items-center rounded-lg",
                                        )}
                                      >
                                        <Icon className="h-5 w-5" />
                                      </span>
                                      <span className="truncate">
                                        {item.name === "AdminMain"
                                          ? "Admin"
                                          : item.name}
                                      </span>
                                      <ChevronRightIcon
                                        className={classNames(
                                          "ml-auto h-5 w-5 text-gray-400 transition-transform",
                                          open && "rotate-90",
                                        )}
                                      />
                                    </DisclosureButton>

                                    <DisclosurePanel
                                      as="ul"
                                      className="mt-1 space-y-1 pl-11"
                                    >
                                      {item.children
                                        .filter((subItem) =>
                                          accessList.includes(subItem.name),
                                        )
                                        .map((subItem) => {
                                          const subCurrent =
                                            subItem.name.replace(/\s/g, "") ===
                                            page;
                                          const SubIcon =
                                            iconMap[subItem.name] ||
                                            DocumentChartBarIcon;

                                          return (
                                            <li key={subItem.name}>
                                              <a
                                                href={subItem.href}
                                                onClick={() =>
                                                  setMobileOpen(false)
                                                }
                                                aria-current={
                                                  subCurrent
                                                    ? "page"
                                                    : undefined
                                                }
                                                className={classNames(
                                                  subCurrent
                                                    ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-100"
                                                    : "hover:bg-gray-50 text-gray-700",
                                                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                                                )}
                                              >
                                                <SubIcon className="h-4 w-4 text-gray-400" />
                                                <span className="truncate">
                                                  {subItem.name ===
                                                  "PaymentRequestsAdmin"
                                                    ? "User Management"
                                                    : subItem.name}
                                                </span>
                                              </a>
                                            </li>
                                          );
                                        })}
                                    </DisclosurePanel>
                                  </>
                                );
                              }}
                            </Disclosure>
                          </li>
                        );
                      })}
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      </Transition>

      {/* ---------- Main Shell (DESKTOP SIZE KEPT) ---------- */}
      <div className="flex min-h-screen bg-gray-100">
        {/* Sidebar — visible only on lg+; collapsible */}
        <div
          className={classNames(
            "hidden lg:flex flex-shrink-0 flex-col border-r border-gray-200 bg-white transition-all duration-300",
            sidebarCollapsed ? "w-20" : "w-64",
          )}
        >
          {/* Brand */}
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur">
            <div
              className={classNames(
                "flex h-16 items-center",
                sidebarCollapsed ? "justify-center px-2" : "justify-between px-5",
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-sm">
                  <Squares2X2Icon className="h-5 w-5" />
                </div>
                {!sidebarCollapsed && (
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900">
                      Solidarity Hub
                    </div>
                    <div className="text-[11px] text-gray-500">
                      Internal Portal
                    </div>
                  </div>
                )}
              </div>
              {!sidebarCollapsed && (
                <button
                  type="button"
                  onClick={toggleSidebar}
                  aria-label="Collapse sidebar"
                  className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <ChevronDoubleLeftIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            {sidebarCollapsed && (
              <div className="flex justify-center pb-2">
                <button
                  type="button"
                  onClick={toggleSidebar}
                  aria-label="Expand sidebar"
                  className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <ChevronDoubleRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
          </div>

          {/* Profile */}
          {!sidebarCollapsed && (
            <div className="px-5 pt-3 pb-4">
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white px-3 py-2 shadow-sm">
                {profileImgSrc ? (
                  <img
                    src={profileImgSrc}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover bg-gray-100"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 text-xs font-bold text-white">
                    {niceName(sessionStorage.getItem("username") || "U")
                      .split(" ")
                      .map((s) => s[0])
                      .slice(0, 2)
                      .join("")}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900">
                    {niceName(sessionStorage.getItem("username") || "")}
                  </div>
                  <div className="truncate text-xs text-gray-500">
                    {sessionStorage.getItem("department") || ""}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Home link */}
          <div className={classNames("px-3", sidebarCollapsed ? "pt-3" : "")}>
            <a
              href="?page=Home"
              title="Home"
              aria-current={page === "Home" ? "page" : undefined}
              className={classNames(
                page === "Home"
                  ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-100"
                  : "text-gray-700 hover:bg-gray-50",
                "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                sidebarCollapsed && "justify-center",
              )}
            >
              <span
                className={classNames(
                  page === "Home"
                    ? "bg-indigo-100 text-indigo-600"
                    : "bg-gray-100 text-gray-500 group-hover:text-gray-600",
                  "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                )}
              >
                <HomeIcon className="h-5 w-5" />
              </span>
              {!sidebarCollapsed && <span className="truncate">Home</span>}
            </a>
          </div>

          {/* Sections label */}
          {!sidebarCollapsed && (
            <div className="px-5 pt-4 pb-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Navigation
              </div>
            </div>
          )}
          {sidebarCollapsed && <div className="my-3 mx-3 h-px bg-gray-100" />}

          {/* Nav (scrollable) */}
          <nav className="flex-1 overflow-y-auto px-3 pb-6">
            <ul role="list" className="space-y-1">
              {navigation
                .filter((item) => accessList.includes(item.name))
                .map((item) => {
                  const isCurrent = item.name.replace(/\s/g, "") === page;
                  const Icon =
                    iconMap[item.name] ||
                    iconMap[item?.parent] ||
                    ChartBarSquareIcon;

                  if (!item.children) {
                    return (
                      <li key={item.name}>
                        <a
                          href={`?page=${item.name.replace(/\s/g, "")}`}
                          aria-current={isCurrent ? "page" : undefined}
                          title={sidebarCollapsed ? item.name : undefined}
                          className={classNames(
                            isCurrent
                              ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-100"
                              : "text-gray-700 hover:bg-gray-50",
                            "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                            sidebarCollapsed && "justify-center",
                          )}
                        >
                          <span
                            className={classNames(
                              isCurrent
                                ? "bg-indigo-100 text-indigo-600"
                                : "bg-gray-100 text-gray-500 group-hover:text-gray-600",
                              "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                            )}
                          >
                            <Icon className="h-5 w-5" />
                          </span>
                          {!sidebarCollapsed && (
                            <span className="truncate">{item.name}</span>
                          )}
                        </a>
                      </li>
                    );
                  }

                  // With children (collapsible)
                  return (
                    <li key={item.name} className="rounded-lg">
                      <Disclosure>
                        {({ open }) => {
                          const parentActive =
                            open ||
                            item.children.some(
                              (c) => c.name.replace(/\s/g, "") === page,
                            );
                          return (
                            <>
                              <DisclosureButton
                                title={
                                  sidebarCollapsed
                                    ? item.name === "AdminMain"
                                      ? "Admin"
                                      : item.name
                                    : undefined
                                }
                                className={classNames(
                                  parentActive
                                    ? "bg-gray-50"
                                    : "hover:bg-gray-50",
                                  "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                                  sidebarCollapsed && "justify-center",
                                )}
                              >
                                <span
                                  className={classNames(
                                    parentActive
                                      ? "bg-indigo-50 text-indigo-600"
                                      : "bg-gray-100 text-gray-500 group-hover:text-gray-600",
                                    "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                                  )}
                                >
                                  <Icon className="h-5 w-5" />
                                </span>
                                {!sidebarCollapsed && (
                                  <>
                                    <span className="truncate">
                                      {item.name === "AdminMain"
                                        ? "Admin"
                                        : item.name}
                                    </span>
                                    <ChevronRightIcon
                                      aria-hidden="true"
                                      className={classNames(
                                        "ml-auto h-5 w-5 text-gray-400 transition-transform",
                                        open && "rotate-90",
                                      )}
                                    />
                                  </>
                                )}
                              </DisclosureButton>

                              <DisclosurePanel
                                as="ul"
                                className={classNames(
                                  "mt-1 space-y-1 pl-11",
                                  sidebarCollapsed && "hidden",
                                )}
                              >
                                {item.children
                                  .filter((subItem) =>
                                    accessList.includes(subItem.name),
                                  )
                                  .map((subItem) => {
                                    const subCurrent =
                                      subItem.name.replace(/\s/g, "") === page;
                                    // Pick icons for children if you want different visuals per-subitem
                                    const SubIcon =
                                      iconMap[subItem.name] ||
                                      DocumentChartBarIcon;

                                    return (
                                      <li key={subItem.name}>
                                        <a
                                          href={subItem.href}
                                          aria-current={
                                            subCurrent ? "page" : undefined
                                          }
                                          className={classNames(
                                            subCurrent
                                              ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-100"
                                              : "hover:bg-gray-50 text-gray-700",
                                            "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                                          )}
                                        >
                                          <SubIcon className="h-4 w-4 text-gray-400" />
                                          <span className="truncate">
                                            {subItem.name ===
                                            "PaymentRequestsAdmin"
                                              ? "User Management"
                                              : subItem.name}
                                          </span>
                                        </a>
                                      </li>
                                    );
                                  })}
                              </DisclosurePanel>
                            </>
                          );
                        }}
                      </Disclosure>
                    </li>
                  );
                })}
            </ul>
          </nav>

          {/* Footer */}
          {!sidebarCollapsed && (
            <div className="mt-auto px-5 pb-4">
              <div className="h-px w-full bg-gray-100 mb-3" />
              <p className="text-[11px] text-gray-400">
                © {new Date().getFullYear()} Solidarity
              </p>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top bar — SAME width container as original */}
          <header className="sticky top-0 z-30 bg-white/95 shadow-sm backdrop-blur">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Mobile hamburger */}
                  <div className="lg:hidden">
                    <button
                      type="button"
                      className="rounded-md p-2 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => setMobileOpen(true)}
                      aria-label="Open navigation"
                    >
                      <Bars3Icon className="h-6 w-6" />
                    </button>
                  </div>

                  <nav className="hidden sm:flex items-center gap-6">
                    {HubsAccess.filter(
                      (item) =>
                        !item.departments ||
                        item.departments.includes(
                          sessionStorage.getItem("department"),
                        ),
                    ).map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        aria-current={item.current ? "page" : undefined}
                        className={classNames(
                          item.current
                            ? "border-indigo-500 text-gray-900"
                            : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900",
                          "inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium",
                        )}
                      >
                        {item.name}
                      </a>
                    ))}
                  </nav>
                </div>

                <div className="hidden sm:flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/hub?page=Home")}
                    title="Go to home"
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <HomeIcon className="h-4 w-4" />
                    Home
                  </button>

                  <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600">
                    <span className="font-medium text-gray-900">Session:</span>{" "}
                    <span className="tabular-nums">{timeLeft}</span>
                  </div>

                  <button
                    type="button"
                    className="rounded-full p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <span className="sr-only">View notifications</span>
                    <BellIcon aria-hidden="true" className="h-6 w-6" />
                  </button>

                  <Menu as="div" className="relative">
                    <MenuButton className="inline-flex items-center justify-center rounded-full p-1 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                      <span className="sr-only">Open user menu</span>
                      <CogIcon aria-hidden="true" className="h-6 w-6" />
                    </MenuButton>
                    <MenuItems
                      transition
                      className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-150 data-[leave]:duration-100"
                    >
                      {userNavigation.map((item) => (
                        <MenuItem key={item.name}>
                          <button
                            onClick={item.action}
                            className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100"
                          >
                            {item.name}
                          </button>
                        </MenuItem>
                      ))}
                    </MenuItems>
                  </Menu>
                </div>
              </div>
            </div>
          </header>

          <main className="flex-1">{pageContent}</main>
        </div>

        {/* Session expired modal */}
        <Modal open={open} setOpen={setOpen} handleSignOut={handleSignOut} />

        {/* Session reminder modal (10 min / 3 min before token expiry) */}
        <SessionReminderModal
          open={reminderMinutes !== null}
          minutesLeft={reminderMinutes}
          onClose={resetReminder}
        />

        {/* Idle timeout warning (14 min idle, 60s grace before auto-logout) */}
        <IdleWarningModal
          open={idleWarning}
          countdown={idleCountdown}
          onStay={resetIdle}
          onLogout={handleIdleLogout}
        />
      </div>
    </>
  );
};

/* ----------------- small pieces ----------------- */
function Card({ children }) {
  return <div className="rounded-xl border border-gray-100">{children}</div>;
}

export default TheHub;
